import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaw,
  faWalking,
  faTaxi,
  faPlayCircle,
  faCheck,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { routes } from '../config/utility';

const Guide = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedGuide, setSelectedGuide] = useState(
    location.search ? new URLSearchParams(location.search).get('type') : 'walk'
  );

  const guides = {
    walk: {
      title: "How to Book a Paw Walk 🚶‍♂️",
      video: "https://www.youtube.com/embed/your-walk-video-id",
      steps: [
        "Sign in to your PawDesh account",
        "Click on 'Book Walk' from the sidebar",
        "Select your pet(s) for the walk",
        "Choose your preferred scheduling option",
        "Select additional services if needed",
        "Review and confirm your booking",
        "Choose your payment method",
        "Track your walker in real-time"
      ],
      tips: [
        "Book at least 2 hours in advance",
        "Make sure your pet's profile is up to date",
        "Add special instructions if needed",
        "Enable notifications for updates"
      ]
    },
    taxi: {
      title: "How to Book a Paw Taxi 🚕",
      video: "https://www.youtube.com/embed/your-taxi-video-id",
      steps: [
        "Log into your PawDesh account",
        "Select 'Book Taxi' from the menu",
        "Choose your pickup and dropoff locations",
        "Select your pet(s) for transport",
        "Choose scheduling time (instant/scheduled)",
        "Add any special requirements",
        "Confirm booking and payment method",
        "Track your pet's journey"
      ],
      tips: [
        "Have your pet ready 5 minutes before pickup",
        "Ensure carrier/cage requirements are met",
        "Keep emergency contact info updated",
        "Save frequent locations for quick booking"
      ]
    },
    pet: {
      title: "Managing Your Pets 🐾",
      video: "https://www.youtube.com/embed/your-pet-video-id",
      steps: [
        "Navigate to 'My Pets' section",
        "Click 'Add New Pet' button",
        "Upload clear photos of your pet",
        "Fill in pet details and medical info",
        "Add vaccination records",
        "Set dietary requirements",
        "Save emergency contact info",
        "Update pet profile regularly"
      ],
      tips: [
        "Keep vaccination records current",
        "Update pet photos regularly",
        "Include any behavioral notes",
        "Maintain accurate weight information"
      ]
    }
  };

  useEffect(() => {
    // Update URL when selection changes
    navigate(`${routes.guide}?type=${selectedGuide}`, { replace: true });
  }, [selectedGuide, navigate]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      {/* Guide Type Selection */}
      <div className="flex flex-wrap justify-center gap-4 mb-8">
        {Object.keys(guides).map((type) => (
          <label
            key={type}
            className={`
              flex items-center space-x-2 px-6 py-3 rounded-full cursor-pointer
              transition-all duration-300 transform hover:scale-105
              ${selectedGuide === type 
                ? 'bg-purple-600 text-white shadow-lg scale-105' 
                : 'bg-white text-gray-700 hover:bg-purple-50'}
            `}
          >
            <input
              type="radio"
              name="guideType"
              value={type}
              checked={selectedGuide === type}
              onChange={(e) => setSelectedGuide(e.target.value)}
              className="hidden"
            />
            <FontAwesomeIcon 
              icon={type === 'walk' ? faWalking : type === 'taxi' ? faTaxi : faPaw}
              className="text-xl"
            />
            <span className="font-medium capitalize">{type}</span>
          </label>
        ))}
      </div>

      {/* Guide Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={selectedGuide}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="space-y-8"
        >
          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-center text-purple-700">
            {guides[selectedGuide].title}
          </h1>

          {/* Video Section */}
          <div className="relative aspect-video rounded-xl overflow-hidden shadow-xl">
            <iframe
              src={guides[selectedGuide].video}
              title={`Guide for ${selectedGuide}`}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>

          {/* Steps and Tips Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Steps Section */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h2 className="text-2xl font-bold text-purple-700 mb-6 flex items-center">
                <FontAwesomeIcon icon={faPlayCircle} className="mr-3" />
                Step by Step Guide
              </h2>
              <div className="space-y-4">
                {guides[selectedGuide].steps.map((step, index) => (
                  <div 
                    key={index}
                    className="flex items-start space-x-3 text-gray-700"
                  >
                    <div className="bg-purple-100 text-purple-700 rounded-full p-2 flex-shrink-0">
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                    <p className="text-lg">{step}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tips Section */}
            <div className="bg-purple-50 rounded-xl p-6 shadow-lg">
              <h2 className="text-2xl font-bold text-purple-700 mb-6 flex items-center">
                <FontAwesomeIcon icon={faInfoCircle} className="mr-3" />
                Pro Tips
              </h2>
              <div className="space-y-4">
                {guides[selectedGuide].tips.map((tip, index) => (
                  <div 
                    key={index}
                    className="flex items-start space-x-3 text-gray-700"
                  >
                    <div className="bg-purple-200 text-purple-700 rounded-full p-2 flex-shrink-0">
                      <FontAwesomeIcon icon={faPaw} />
                    </div>
                    <p className="text-lg">{tip}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Guide;